import React, { useState, useMemo } from "react";
import { Modal, Form, Select, InputNumber, Button, Space, Statistic, Row, Col, Card, Radio, Divider, Alert } from "antd";
import { DownloadOutlined, MailOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  buildSegmentQuery,
  filterCustomers,
  getExportStats,
  cleanCustomersForExport,
  exportCustomersToCSV,
  exportCustomersToMailchimp,
} from "utils/segmentExport";

const { Option } = Select;

const SegmentExportModal = ({ visible, onClose, customers, segments }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [exportFormat, setExportFormat] = useState("csv");

  // Get filtered customers based on form values
  const filteredCustomers = useMemo(() => {
    const values = form.getFieldsValue();
    const query = buildSegmentQuery(values);
    const filtered = filterCustomers(customers, query);
    return cleanCustomersForExport(filtered);
  }, [customers, form]);

  // Get export statistics
  const stats = useMemo(() => {
    return getExportStats(filteredCustomers);
  }, [filteredCustomers]);

  const handleExport = () => {
    const values = form.getFieldsValue();
    const segmentName = values.segment || "all_customers";

    if (filteredCustomers.length === 0) {
      Modal.warning({
        title: t("no_customers_to_export"),
        content: t("please_adjust_filters"),
      });
      return;
    }

    if (exportFormat === "csv") {
      exportCustomersToCSV(filteredCustomers, segmentName);
    } else if (exportFormat === "mailchimp") {
      exportCustomersToMailchimp(filteredCustomers, segmentName);
    }

    Modal.success({
      title: t("export_successful"),
      content: t("exported_customers_count", { count: filteredCustomers.length }),
    });

    onClose();
  };

  const handleFormChange = () => {
    // Force re-render to update stats
    form.validateFields();
  };

  return (
    <Modal
      title={t("export_customer_segment")}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("cancel")}
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={exportFormat === "mailchimp" ? <MailOutlined /> : <DownloadOutlined />}
          onClick={handleExport}
          disabled={filteredCustomers.length === 0}
        >
          {t("export")} ({filteredCustomers.length})
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onValuesChange={handleFormChange} initialValues={{ shopSource: "all" }}>
        {/* Export Format */}
        <Form.Item label={t("export_format")} name="exportFormat">
          <Radio.Group value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
            <Radio.Button value="csv">
              <DownloadOutlined /> CSV
            </Radio.Button>
            <Radio.Button value="mailchimp">
              <MailOutlined /> Mailchimp
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Divider>{t("filters")}</Divider>

        {/* Segment Filter */}
        <Form.Item label={t("rfm_segment")} name="segment">
          <Select placeholder={t("all_segments")} allowClear>
            {segments.map((segment) => (
              <Option key={segment} value={segment}>
                {segment}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Shop Source Filter */}
        <Form.Item label={t("shop_source")} name="shopSource">
          <Select>
            <Option value="all">{t("all")}</Option>
            <Option value="VCE">VCE</Option>
            <Option value="HVO">HVO</Option>
          </Select>
        </Form.Item>

        <Row gutter={16}>
          {/* Order Count Filters */}
          <Col span={12}>
            <Form.Item label={t("min_orders")} name="minOrders">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={t("no_minimum")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t("max_orders")} name="maxOrders">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={t("no_maximum")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Spent Filters */}
          <Col span={12}>
            <Form.Item label={t("min_spent")} name="minSpent">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={t("no_minimum")} prefix="€" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t("max_spent")} name="maxSpent">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={t("no_maximum")} prefix="€" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Recency Filters (days since last order) */}
          <Col span={12}>
            <Form.Item label={t("min_recency_days")} name="minRecency">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={t("no_minimum")} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t("max_recency_days")} name="maxRecency">
              <InputNumber min={0} style={{ width: "100%" }} placeholder={t("no_maximum")} />
            </Form.Item>
          </Col>
        </Row>

        {/* Campaign Interaction Filter */}
        <Form.Item label={t("campaign_interaction")} name="campaignInteraction" valuePropName="checked">
          <Select placeholder={t("all_customers")} allowClear>
            <Option value={true}>{t("only_campaign_customers")}</Option>
            <Option value={false}>{t("only_non_campaign_customers")}</Option>
          </Select>
        </Form.Item>

        <Divider>{t("export_preview")}</Divider>

        {/* Export Statistics */}
        <Card>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic title={t("customers_to_export")} value={stats.totalCustomers} />
            </Col>
            <Col span={8}>
              <Statistic title={t("total_revenue")} value={stats.totalRevenue} prefix="€" precision={2} />
            </Col>
            <Col span={8}>
              <Statistic title={t("avg_order_value")} value={stats.averageOrderValue} prefix="€" precision={2} />
            </Col>
          </Row>

          {/* Shop Breakdown */}
          {Object.keys(stats.shopBreakdown).length > 0 && (
            <>
              <Divider />
              <div>
                <strong>{t("shop_breakdown")}:</strong>
                <div style={{ marginTop: 8 }}>
                  {Object.entries(stats.shopBreakdown).map(([shop, count]) => (
                    <div key={shop}>
                      {shop}: {count} {t("customers")}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Segment Breakdown */}
          {Object.keys(stats.segmentBreakdown).length > 0 && (
            <>
              <Divider />
              <div>
                <strong>{t("segment_breakdown")}:</strong>
                <div style={{ marginTop: 8 }}>
                  {Object.entries(stats.segmentBreakdown).map(([segment, count]) => (
                    <div key={segment}>
                      {segment}: {count} {t("customers")}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Mailchimp Instructions */}
        {exportFormat === "mailchimp" && (
          <Alert
            message={t("mailchimp_export_instructions")}
            description={
              <div>
                <p>{t("mailchimp_csv_format_description")}</p>
                <ol>
                  <li>{t("mailchimp_step_1")}</li>
                  <li>{t("mailchimp_step_2")}</li>
                  <li>{t("mailchimp_step_3")}</li>
                </ol>
              </div>
            }
            type="info"
            style={{ marginTop: 16 }}
          />
        )}
      </Form>
    </Modal>
  );
};

export default SegmentExportModal;
